import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Bell, X, Music, Sparkles, Star, Megaphone, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import ReactMarkdown from 'react-markdown';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeConfig = {
  audio: { icon: Music, label: 'Nouveau contenu audio', color: 'text-purple-500', bg: 'bg-purple-100' },
  improvement: { icon: Sparkles, label: 'Amélioration', color: 'text-blue-500', bg: 'bg-blue-100' },
  new_feature: { icon: Star, label: 'Nouveauté', color: 'text-amber-500', bg: 'bg-amber-100' },
  general: { icon: Megaphone, label: 'Annonce', color: 'text-sage-500', bg: 'bg-sage-100' }
};

export const NotificationBell = () => {
  const { token } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchUnreadCount = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/announcements/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(res.data.unread_count);
    } catch (error) {
      console.error('Erreur chargement compteur:', error);
    }
  };

  const fetchAnnouncements = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(res.data);
    } catch (error) {
      console.error('Erreur chargement annonces:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (announcementId) => {
    try {
      await axios.post(`${API_URL}/announcements/${announcementId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(prev => 
        prev.map(a => a.id === announcementId ? { ...a, is_read: true } : a)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Erreur marquage lu:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API_URL}/announcements/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(prev => prev.map(a => ({ ...a, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Erreur marquage tous lus:', error);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    // Rafraîchir toutes les 5 minutes
    const interval = setInterval(fetchUnreadCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  const handleOpen = () => {
    setIsOpen(true);
    fetchAnnouncements();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <>
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-full hover:bg-sage-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-sage-600" />
        {unreadCount > 0 && (
          <span 
            className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md mx-4 bg-white rounded-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="notifications-modal">
          <DialogHeader className="pb-2 border-b border-sand-100">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-serif font-light text-sage-700">
                Notifications
              </DialogTitle>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-sage-500 hover:text-sage-700 text-sm"
                  data-testid="mark-all-read"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Tout marquer lu
                </Button>
              )}
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-sage-500">Chargement...</p>
              </div>
            ) : announcements.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Bell className="w-12 h-12 text-sage-300 mb-3" />
                <p className="text-sage-500">Aucune notification</p>
                <p className="text-sage-400 text-sm">Vous serez informé des nouveautés ici</p>
              </div>
            ) : (
              <div className="space-y-2">
                {announcements.map((announcement) => {
                  const config = typeConfig[announcement.announcement_type] || typeConfig.general;
                  const Icon = config.icon;
                  
                  return (
                    <div
                      key={announcement.id}
                      className={`p-4 rounded-xl border transition-colors cursor-pointer ${
                        announcement.is_read 
                          ? 'bg-white border-sand-100' 
                          : 'bg-sage-50 border-sage-200'
                      }`}
                      onClick={() => !announcement.is_read && markAsRead(announcement.id)}
                      data-testid={`announcement-${announcement.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${config.bg}`}>
                          <Icon className={`w-4 h-4 ${config.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className={`text-xs font-medium ${config.color}`}>
                              {config.label}
                            </span>
                            <span className="text-xs text-sage-400">
                              {formatDate(announcement.created_at)}
                            </span>
                          </div>
                          <h4 className={`font-sans font-medium text-sage-700 mb-1 ${
                            !announcement.is_read ? 'font-semibold' : ''
                          }`}>
                            {announcement.title}
                          </h4>
                          <div className="text-sm text-sage-600 prose-sm">
                            <ReactMarkdown>{announcement.content}</ReactMarkdown>
                          </div>
                        </div>
                        {!announcement.is_read && (
                          <div className="w-2 h-2 bg-sage-500 rounded-full mt-2 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationBell;
